import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { brandPrefersSimple } from "../theme";
import { getActiveBrand } from "../branding/activeBrand";

interface SimpleModeContextType {
  simpleMode: boolean;
  setSimpleMode: (isSimple: boolean) => void;
}

export const SimpleModeContext = createContext<SimpleModeContextType>({
  simpleMode: false,
  setSimpleMode: () => {},
});

export const useSimpleMode = () => useContext(SimpleModeContext);

interface SimpleModeProviderProps {
  children: ReactNode;
}

/**
 * "Simple / accessible mode" — a device-local preference, seeded from the brand default
 * (CerebraLinked memory-care = ON) and overridable in Preferences. Mirrors DarkModeContext:
 * there is deliberately no server-side preference store. The preference is applied to the
 * theme only for the patient audience (see AppEntry), so clinical/external staff on the same
 * cell keep the standard dense UI.
 */
export const SimpleModeProvider = ({ children }: SimpleModeProviderProps) => {
  const [simpleMode, setSimpleModeState] = useState(() => {
    const saved = localStorage.getItem("simpleMode.v1");
    return saved != null ? JSON.parse(saved) : brandPrefersSimple(getActiveBrand().key);
  });

  useEffect(() => {
    localStorage.setItem("simpleMode.v1", JSON.stringify(simpleMode));
  }, [simpleMode]);

  const setSimpleMode = (isSimple: boolean) => {
    setSimpleModeState(isSimple);
  };

  return (
    <SimpleModeContext.Provider value={{ simpleMode, setSimpleMode }}>
      {children}
    </SimpleModeContext.Provider>
  );
};
